// src/components/admin/ImageUpload.js
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom'; // Import Link for navigation

const API_BASE = process.env.REACT_APP_API_URL;

// Define categories to match your LandingPage cards for consistency
const categories = [
  { name: 'Interior Painting', slug: 'interior-painting' },
  { name: 'Exterior Painting', slug: 'exterior-painting' },
  { name: 'Protective Coatings', slug: 'protective-coatings' },
  { name: 'Color Consultation & Matching', slug: 'color-consultation-matching' },
  { name: 'Wall Repair & Surface Preparation', slug: 'wall-repair' },
  { name: 'Ceiling Designing', slug: 'ceiling-designing' },
];

const ImageUpload = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');

  const handleChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    if (selectedFile) {
      setPreview(URL.createObjectURL(selectedFile));
    } else {
      setPreview('');
    }
    setMessage('');
  };

  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    setMessage('');
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setMessage('Please select a file to upload.');
      return;
    }
    if (!title) {
      setMessage('Please enter an image title.');
      return;
    }
    if (!category) {
      setMessage('Please select a category for the image.');
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('title', title);
    formData.append('category', category);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/api/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true
      });
      setMessage('✅ Image uploaded successfully!');
      setFile(null);
      setPreview('');
      setTitle('');
      setCategory('');
      // No longer need to fetch images here, ImageManager handles it
    } catch (err) {
      console.error('Upload failed:', err.response?.data || err.message, err.response?.status);
      setMessage('❌ Upload failed. Please try again.');
      if (err.response?.status === 401 || err.response?.status === 403) {
        setMessage('❌ Upload failed. Authentication required or unauthorized.');
      } else if (err.response?.status === 404) {
        setMessage('❌ Upload failed. API route not found. Check backend.');
      } else if (err.response?.status === 500) {
        setMessage('❌ Upload failed. Server error. Check backend logs for details.');
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">Upload New Image</h2>
      <form onSubmit={handleSubmit} className="space-y-4 mb-8">
        <input
          type="text"
          placeholder="Image Title"
          value={title}
          onChange={handleTitleChange}
          required
          className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-cyan-400"
        />
        <select
          value={category}
          onChange={handleCategoryChange}
          required
          className="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-cyan-400 bg-white text-gray-700"
        >
          <option value="">Select Category</option>
          {categories.map((cat) => (
            <option key={cat.slug} value={cat.slug}>
              {cat.name}
            </option>
          ))}
        </select>
        <input type="file" accept="image/*" onChange={handleChange} className="p-2 border rounded" />
        {preview && <img src={preview} alt="Preview" className="w-32 mt-2 rounded" />}
        <button type="submit" className="bg-cyan-600 text-white px-4 py-2 rounded hover:bg-cyan-700">
          Upload
        </button>
        {message && <p className={`mt-2 ${message.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>}
      </form>

      {/* Link to the Image Management section */}
      <div className="mt-8 pt-4 border-t border-gray-200">
        <h2 className="text-xl font-bold mb-4">Manage Existing Images</h2>
        <p className="text-gray-700 mb-4">
          View, edit, or delete previously uploaded images in the dedicated image management section.
        </p>
        <Link
          to="/admin/image-manager" // Define this route in App.js
          className="inline-block bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 font-semibold"
        >
          Go to Image Manager
        </Link>
      </div>
    </div>
  );
};

export default ImageUpload;